import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';

interface BodyScrollStyles {
  position: string;
  top: string;
  left: string;
  right: string;
  width: string;
}

@Injectable({ providedIn: 'root' })
export class PageScrollLockService {
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly locks = new Set<string>();
  private scrollY = 0;
  private previousStyles: BodyScrollStyles = {
    position: '',
    top: '',
    left: '',
    right: '',
    width: '',
  };

  lock(token: string): void {
    if (!isPlatformBrowser(this.platformId) || this.locks.has(token)) return;

    const window = this.document.defaultView;
    const body = this.document.body;
    const documentElement = this.document.documentElement;
    if (!window || !body || !documentElement) return;

    this.locks.add(token);
    if (this.locks.size > 1) return;

    const bodyStyle = body.style;
    this.scrollY = window.scrollY;
    this.previousStyles = {
      position: bodyStyle.position,
      top: bodyStyle.top,
      left: bodyStyle.left,
      right: bodyStyle.right,
      width: bodyStyle.width,
    };

    // Keep left/right at 0 — using -scrollX breaks dir=rtl and shifts the whole PDP.
    bodyStyle.position = 'fixed';
    bodyStyle.top = `-${this.scrollY}px`;
    bodyStyle.left = '0';
    bodyStyle.right = '0';
    bodyStyle.width = '100%';
    documentElement.classList.add('iho-page-scroll-locked');
  }

  unlock(token: string): void {
    if (!this.locks.delete(token) || this.locks.size > 0) return;
    this.restorePageScroll();
  }

  clear(): void {
    if (!this.locks.size) return;
    this.locks.clear();
    this.restorePageScroll();
  }

  /** Clears leftover CDK/dialog horizontal shift (common after MatDialog on RTL). */
  restoreViewport(scrollY?: number): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const window = this.document.defaultView;
    const body = this.document.body;
    const documentElement = this.document.documentElement;
    if (!window || !body || !documentElement) return;

    documentElement.classList.remove('cdk-global-scrollblock');
    documentElement.style.removeProperty('left');
    documentElement.style.removeProperty('right');
    documentElement.style.removeProperty('top');

    if (!this.locks.size) {
      body.style.removeProperty('left');
      body.style.removeProperty('right');
      if (
        body.style.position === 'fixed' &&
        !documentElement.classList.contains('iho-page-scroll-locked')
      ) {
        body.style.removeProperty('position');
        body.style.removeProperty('top');
        body.style.removeProperty('width');
      }
    }

    const top = scrollY ?? window.scrollY;
    if (typeof window.scrollTo === 'function') {
      window.scrollTo({ top, left: 0, behavior: 'instant' as ScrollBehavior });
    }
  }

  private restorePageScroll(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const window = this.document.defaultView;
    const body = this.document.body;
    const documentElement = this.document.documentElement;
    if (!window || !body || !documentElement) return;

    const bodyStyle = body.style;
    bodyStyle.position = this.previousStyles.position;
    bodyStyle.top = this.previousStyles.top;
    bodyStyle.left = this.previousStyles.left;
    bodyStyle.right = this.previousStyles.right;
    bodyStyle.width = this.previousStyles.width;
    documentElement.classList.remove('iho-page-scroll-locked');

    const htmlStyle = documentElement.style;
    const htmlScrollBehavior = htmlStyle.scrollBehavior;
    const bodyScrollBehavior = bodyStyle.scrollBehavior;
    htmlStyle.scrollBehavior = 'auto';
    bodyStyle.scrollBehavior = 'auto';
    if (typeof window.scrollTo === 'function') {
      // Always restore horizontal position to 0 (RTL + fixed body can leave a shift).
      window.scrollTo({ left: 0, top: this.scrollY, behavior: 'instant' as ScrollBehavior });
    }
    htmlStyle.scrollBehavior = htmlScrollBehavior;
    bodyStyle.scrollBehavior = bodyScrollBehavior;
  }
}
